import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { apiFetch, type ApiError } from '../../lib/api.js';
import { useAuth } from '../../context/AuthContext.js';
import { useModActions } from '../../hooks/useModActions.js';

const MAX_LEN = 500;

interface SendResponse {
  ok: boolean;
  source: string;
}

interface SlashCmd {
  cmd: string;
  syntax: string;
  desc: string;
}

const SLASH_COMMANDS: SlashCmd[] = [
  { cmd: 'timeout',        syntax: '/timeout <user> [sek] [grund]', desc: 'Benutzer temporär sperren' },
  { cmd: 'ban',            syntax: '/ban <user> [grund]',           desc: 'Benutzer dauerhaft sperren' },
  { cmd: 'unban',          syntax: '/unban <user>',                 desc: 'Sperre/Timeout aufheben' },
  { cmd: 'untimeout',      syntax: '/untimeout <user>',             desc: 'Timeout aufheben' },
  { cmd: 'slow',           syntax: '/slow [sekunden]',              desc: 'Slow-Mode aktivieren' },
  { cmd: 'slowoff',        syntax: '/slowoff',                      desc: 'Slow-Mode deaktivieren' },
  { cmd: 'subscribers',    syntax: '/subscribers',                  desc: 'Sub-Only-Modus an' },
  { cmd: 'subscribersoff', syntax: '/subscribersoff',               desc: 'Sub-Only-Modus aus' },
  { cmd: 'emoteonly',      syntax: '/emoteonly',                    desc: 'Emote-Only an' },
  { cmd: 'emoteonlyoff',   syntax: '/emoteonlyoff',                 desc: 'Emote-Only aus' },
  { cmd: 'uniquechat',     syntax: '/uniquechat',                   desc: 'Unique-Chat an' },
  { cmd: 'uniquechatoff',  syntax: '/uniquechatoff',                desc: 'Unique-Chat aus' },
  { cmd: 'followers',      syntax: '/followers [minuten]',          desc: 'Follower-Only an' },
  { cmd: 'followersoff',   syntax: '/followersoff',                 desc: 'Follower-Only aus' },
];

export interface ReplyTarget {
  msgId: string;
  displayName: string;
  msgBody: string;
}

interface Props {
  replyTo?: ReplyTarget | null;
  onClearReply?: () => void;
}

export default function ChatInput({ replyTo, onClearReply }: Props) {
  const { isLoggedIn, isMod, isBroadcaster } = useAuth();
  const { patchChatSettings, unbanUser } = useModActions();
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<SlashCmd[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Compute suggestions whenever content changes
  const updateSuggestions = useCallback((val: string) => {
    if (!(isMod || isBroadcaster)) { setSuggestions([]); return; }
    // Only suggest while still typing the command name (no space yet after /)
    const match = /^\/(\S*)$/.exec(val);
    if (!match) { setSuggestions([]); return; }
    const prefix = match[1]!.toLowerCase();
    const filtered = SLASH_COMMANDS.filter((c) => c.cmd.startsWith(prefix));
    setSuggestions(filtered);
    setSelectedIdx(0);
  }, [isMod, isBroadcaster]);

  const applySuggestion = useCallback((cmd: SlashCmd) => {
    // Commands with arguments get a trailing space; toggle-style commands don't need one
    const hasArgs = cmd.syntax.includes('<') || cmd.syntax.includes('[');
    setContent(`/${cmd.cmd}${hasArgs ? ' ' : ''}`);
    setSuggestions([]);
    textareaRef.current?.focus();
  }, []);

  // Focus textarea when a reply target is set
  useEffect(() => {
    if (replyTo) textareaRef.current?.focus();
  }, [replyTo]);

  const handleSend = useCallback(async () => {
    const trimmed = content.trim();
    if (!trimmed || sending) return;

    // ── Slash commands ──────────────────────────────────────────────────────────
    if ((isMod || isBroadcaster) && trimmed.startsWith('/')) {
      const result = await handleSlashCommand(trimmed, { patchChatSettings, unbanUser });
      if (result !== 'not_a_command') {
        setSending(false);
        if (result === 'ok') {
          setContent('');
        } else {
          setError(result);
        }
        textareaRef.current?.focus();
        return;
      }
      // Unknown slash command — let Twitch handle it (fall through to send)
    }

    // Broadcaster-only !resetchat command — clears chat history for all viewers
    if (trimmed === '!resetchat') {
      if (!isBroadcaster) return;
      setSending(true);
      setError(null);
      try {
        await apiFetch('/api/mod/chat-reset', { method: 'POST' });
        setContent('');
      } catch {
        setError('Chat-Reset fehlgeschlagen');
      } finally {
        setSending(false);
        textareaRef.current?.focus();
      }
      return;
    }

    if (trimmed.length > MAX_LEN) {
      setError(`Maximal ${MAX_LEN} Zeichen.`);
      return;
    }
    setSending(true);
    setError(null);
    try {
      await apiFetch<SendResponse>('/api/messages', {
        method: 'POST',
        body: JSON.stringify({
          content: trimmed,
          sources: ['twitch'],
          ...(replyTo ? { replyParentMsgId: replyTo.msgId } : {}),
        }),
      });
      setContent('');
      onClearReply?.();
    } catch (err) {
      const apiErr = err as ApiError;
      const body = apiErr?.body as { error?: string; message?: string } | null;
      setError(body?.message || mapErrorCode(body?.error) || 'Senden fehlgeschlagen');
    } finally {
      setSending(false);
      textareaRef.current?.focus();
    }
  }, [content, sending, isBroadcaster, replyTo, onClearReply]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // Suggestion navigation
      if (suggestions.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIdx((i) => (i + 1) % suggestions.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
          return;
        }
        if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
          e.preventDefault();
          const s = suggestions[selectedIdx];
          if (s) applySuggestion(s);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setSuggestions([]);
          return;
        }
      }

      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void handleSend();
      }
      if (e.key === 'Escape' && replyTo) {
        onClearReply?.();
      }
    },
    [suggestions, selectedIdx, applySuggestion, handleSend, replyTo, onClearReply],
  );

  if (!isLoggedIn) return null;

  const remaining = MAX_LEN - content.length;

  return (
    <div className="border-t border-slate-800 bg-slate-900/60 px-2 py-1.5 flex-shrink-0">
      {replyTo && (
        <div className="flex items-center gap-1.5 mb-1 px-1 py-0.5 rounded bg-slate-800/60 text-xs text-slate-400">
          <svg className="w-3 h-3 flex-shrink-0 text-purple-400" viewBox="0 0 16 16" fill="currentColor">
            <path d="M6 4.5L1 9l5 4.5V11h4c2.2 0 4 1.8 4 4v.5c.7-1 1-2.2 1-3.5 0-3.3-2.7-6-6-6H6V4.5z"/>
          </svg>
          <span className="text-purple-300 font-semibold">@{replyTo.displayName}</span>
          <span className="truncate max-w-[200px] text-slate-500">{replyTo.msgBody}</span>
          <button
            type="button"
            onClick={onClearReply}
            className="ml-auto flex-shrink-0 text-slate-500 hover:text-slate-300"
            title="Antwort abbrechen (Esc)"
          >
            ✕
          </button>
        </div>
      )}
      <div className="relative flex gap-1.5 items-end">
        {suggestions.length > 0 && (
          <div className="absolute bottom-full left-0 right-0 mb-1 z-50 rounded border border-slate-700 bg-slate-900 shadow-lg overflow-hidden">
            {suggestions.map((s, i) => (
              <button
                key={s.cmd}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); applySuggestion(s); }}
                className={`w-full text-left px-2 py-1.5 flex items-baseline gap-2 text-xs ${i === selectedIdx ? 'bg-slate-700' : 'hover:bg-slate-800'}`}
              >
                <span className="font-mono text-purple-400 flex-shrink-0">/{s.cmd}</span>
                <span className="font-mono text-slate-400 flex-shrink-0">{s.syntax.slice(s.cmd.length + 1)}</span>
                <span className="text-slate-500 truncate">{s.desc}</span>
              </button>
            ))}
          </div>
        )}
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => { setContent(e.target.value); updateSuggestions(e.target.value); }}
          onKeyDown={onKeyDown}
          placeholder={replyTo ? `@${replyTo.displayName} antworten…` : 'Nachricht…'}
          maxLength={MAX_LEN + 50}
          rows={1}
          className={`flex-1 resize-none bg-slate-950 border ${content.length > 0 ? 'border-purple-600' : 'border-slate-800'} rounded px-2 py-1.5 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-purple-600 min-h-[32px] max-h-[80px]`}
          disabled={sending}
        />
        <button
          type="button"
          onClick={() => void handleSend()}
          disabled={sending || content.trim().length === 0}
          title="Senden"
          className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded bg-purple-700 hover:bg-purple-600 disabled:bg-slate-800 disabled:text-slate-600 text-white"
        >
          {sending ? (
            <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
            </svg>
          ) : (
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
            </svg>
          )}
        </button>
      </div>
      {error && <div className="mt-1 text-xs text-red-400">{error}</div>}
      {remaining < 50 && (
        <div className="flex justify-end mt-0.5">
          <span className={`text-xs ${remaining < 0 ? 'text-red-400' : 'text-slate-600'}`}>{remaining}</span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Slash command handler
// Returns 'ok' | error-string | 'not_a_command'
// ─────────────────────────────────────────────────────────────────────────────
type SlashDeps = {
  patchChatSettings: (patch: Record<string, unknown>) => Promise<unknown>;
  unbanUser: (targetUserId: string) => Promise<void>;
};

async function handleSlashCommand(raw: string, deps: SlashDeps): Promise<string> {
  const parts = raw.slice(1).split(/\s+/);
  const cmd = parts[0]?.toLowerCase() ?? '';
  const args = parts.slice(1);

  try {
    switch (cmd) {
      // ── User actions ──────────────────────────────────────────────
      case 'timeout': {
        const username = args[0];
        const seconds = args[1] ? parseInt(args[1], 10) : 600;
        const reason = args.slice(2).join(' ') || undefined;
        if (!username) return 'Syntax: /timeout <username> [sekunden] [grund]';
        if (isNaN(seconds) || seconds < 1) return 'Ungültige Dauer.';
        await apiFetch('/api/mod/timeout', {
          method: 'POST',
          body: JSON.stringify({ targetUsername: username, duration: seconds, ...(reason ? { reason } : {}) }),
        });
        return 'ok';
      }
      case 'ban': {
        const username = args[0];
        const reason = args.slice(1).join(' ') || undefined;
        if (!username) return 'Syntax: /ban <username> [grund]';
        await apiFetch('/api/mod/ban', {
          method: 'POST',
          body: JSON.stringify({ targetUsername: username, ...(reason ? { reason } : {}) }),
        });
        return 'ok';
      }
      case 'unban':
      case 'untimeout': {
        const username = args[0];
        if (!username) return `Syntax: /${cmd} <username>`;
        await apiFetch(`/api/mod/ban-by-name/${encodeURIComponent(username)}`, { method: 'DELETE' });
        return 'ok';
      }

      // ── Chat settings ─────────────────────────────────────────────
      case 'slow': {
        const secs = args[0] ? parseInt(args[0], 10) : 30;
        if (isNaN(secs) || secs < 0) return 'Ungültige Dauer.';
        await deps.patchChatSettings({ slow_mode: true, slow_mode_wait_time: secs });
        return 'ok';
      }
      case 'slowoff':
        await deps.patchChatSettings({ slow_mode: false });
        return 'ok';
      case 'subscribers':
        await deps.patchChatSettings({ subscriber_mode: true });
        return 'ok';
      case 'subscribersoff':
        await deps.patchChatSettings({ subscriber_mode: false });
        return 'ok';
      case 'emoteonly':
        await deps.patchChatSettings({ emote_mode: true });
        return 'ok';
      case 'emoteonlyoff':
        await deps.patchChatSettings({ emote_mode: false });
        return 'ok';
      case 'uniquechat':
        await deps.patchChatSettings({ unique_chat_mode: true });
        return 'ok';
      case 'uniquechatoff':
        await deps.patchChatSettings({ unique_chat_mode: false });
        return 'ok';
      case 'followers': {
        const mins = args[0] ? parseInt(args[0], 10) : 0;
        if (isNaN(mins) || mins < 0) return 'Ungültige Dauer.';
        await deps.patchChatSettings({ follower_mode: true, follower_mode_duration: mins });
        return 'ok';
      }
      case 'followersoff':
        await deps.patchChatSettings({ follower_mode: false });
        return 'ok';

      default:
        return 'not_a_command';
    }
  } catch (err) {
    const apiErr = err as ApiError;
    const body = apiErr?.body as { error?: string } | null;
    if (body?.error === 'user_not_found') return 'Benutzer nicht gefunden.';
    return 'Befehl fehlgeschlagen.';
  }
}

function mapErrorCode(code?: string): string | null {
  if (!code) return null;
  switch (code) {
    case 'rate_limited':
      return 'Zu viele Nachrichten — bitte einen Moment warten.';
    case 'subs_only':
      return 'Chat ist im Sub-only-Modus.';
    case 'followers_only':
      return 'Chat ist im Follower-only-Modus.';
    case 'banned':
      return 'Du bist im Channel gebannt.';
    case 'timed_out':
      return 'Du bist getimeoutet.';
    case 'slow_mode':
      return 'Slow-Mode aktiv — kurz warten.';
    case 'duplicate':
      return 'Doppelte Nachricht — bitte abändern.';
    case 'too_long':
      return 'Nachricht ist zu lang.';
    case 'no_token':
    case 'token_refresh_failed':
      return 'Twitch-Login abgelaufen — bitte neu einloggen.';
    default:
      return null;
  }
}
